export interface AmazonLinePreview {
  asin?: string
  title?: string
  seller_name?: string
  quantity?: number | null
  unit_price?: number | null
  item_subtotal?: number | null
  shipping?: number | null
  promotion?: number | null
  tax?: number | null
  net_total?: number | null
  shipment_dates?: string | null
}

export interface AmazonExtractedPayload {
  order_id?: string
  charge_id?: string
  payment_date?: string
  payment_amount?: number | null
  card_last4?: string
  buyer_name?: string
  recipient_name?: string
  ship_to_address?: string
  pending_shipment_amount?: number | null
  lines?: AmazonLinePreview[]
}

function formatMoney(amount: number): string {
  const rounded = Math.round(amount * 100) / 100
  if (rounded === 0) return '$0.00'
  if (rounded < 0) {
    return `($${Math.abs(rounded).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  }
  return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatOptionalMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return formatMoney(Number(value))
}

function formatQty(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return String(value)
}

export function parseAmazonExtractedPayload(
  extractedData: Record<string, unknown> | null | undefined,
): AmazonExtractedPayload | null {
  const raw = extractedData?.amazon
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  return raw as AmazonExtractedPayload
}

interface Props {
  extractedData: Record<string, unknown> | null | undefined
  locationLabel?: string | null
  chargeIncomplete?: boolean
}

export default function AmazonChargePreview({
  extractedData,
  locationLabel,
  chargeIncomplete,
}: Props) {
  const amazon = parseAmazonExtractedPayload(extractedData)
  if (!amazon) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-gray-500">
        No Amazon charge data on this document.
      </div>
    )
  }

  const orderId = amazon.order_id?.trim() || ''
  const orderUrl = orderId
    ? `https://www.amazon.com/gp/css/summary/print.html/?orderID=${encodeURIComponent(orderId)}`
    : null

  const lines = amazon.lines ?? []
  const shipToParts = [amazon.ship_to_address, locationLabel?.trim()].filter(Boolean)
  const shipToDisplay = shipToParts.length > 0 ? shipToParts.join(' · ') : '—'

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-2">
      <div className="shrink-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-gray-500">Order ID</dt>
            <dd className="font-medium text-gray-900">
              {orderUrl ? (
                <a href={orderUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                  {orderId}
                </a>
              ) : (
                orderId || '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500">Charge ID</dt>
            <dd className="text-gray-900">{amazon.charge_id || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500">Payment date</dt>
            <dd className="text-gray-900">{amazon.payment_date || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500">Amount</dt>
            <dd className="tabular-nums text-gray-900">
              {formatOptionalMoney(amazon.payment_amount)}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500">Card</dt>
            <dd className="text-gray-900">
              {amazon.card_last4 ? `····${amazon.card_last4}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500">Buyer</dt>
            <dd className="text-gray-900">{amazon.buyer_name || '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500">Ship to</dt>
            <dd className="text-gray-900">{shipToDisplay}</dd>
          </div>
          {chargeIncomplete && amazon.pending_shipment_amount != null ? (
            <div className="sm:col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <span className="text-xs font-semibold uppercase tracking-wide">Pending shipment</span>
              <span className="ml-2 tabular-nums">{formatMoney(Number(amazon.pending_shipment_amount))}</span>
            </div>
          ) : null}
        </dl>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-gray-100 shadow-[0_1px_0_0_rgb(229,231,235)]">
            <tr>
              {[
                'ASIN',
                'Title',
                'Seller',
                'Qty',
                'Unit price',
                'Subtotal',
                'Shipping',
                'Promo',
                'Tax',
                'Net',
                'Shipment dates',
              ].map((label) => (
                <th
                  key={label}
                  scope="col"
                  className="border-b border-gray-200 px-2 py-2 text-left align-top font-semibold text-gray-800 whitespace-nowrap"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-3 py-6 text-center text-gray-500">
                  No line items in Amazon payload.
                </td>
              </tr>
            ) : (
              lines.map((line, idx) => {
                const asin = line.asin?.trim() || ''
                const asinUrl = asin ? `https://www.amazon.com/dp/${encodeURIComponent(asin)}` : null
                return (
                  <tr key={`${asin}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'}>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top whitespace-nowrap">
                      {asinUrl ? (
                        <a
                          href={asinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 hover:underline"
                        >
                          {asin}
                        </a>
                      ) : (
                        asin || '—'
                      )}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top max-w-[220px] whitespace-normal break-words">
                      {line.title || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top max-w-[140px] truncate" title={line.seller_name || undefined}>
                      {line.seller_name || '—'}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">{formatQty(line.quantity)}</td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.unit_price)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.item_subtotal)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.shipping)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.promotion)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.tax)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top text-right tabular-nums">
                      {formatOptionalMoney(line.net_total)}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1.5 align-top whitespace-nowrap text-gray-700">
                      {line.shipment_dates || '—'}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
